/**
 * The entry-kind seam: every declared kind bound to its slice.
 *
 * **Totality is the point.** `Registry` is a mapped type over `EntryOf`, so a kind in the union
 * with no slice is `TS2741: Property 'label' is missing in type '{ … }' but required in type
 * 'Registry'`, naming the miss at the literal below. That is ADR 0009:55-56's promise, and it is
 * what replaces a four-site obligation stated in prose — before this, adding a ninth kind meant
 * remembering seven row adapters, four card adapters, eight create mutations and seven analysis
 * branches, with nothing to remind you.
 *
 * `Record<EntryType, EntrySlice>` would not do: `EntrySlice` is invariant in its entry parameter,
 * so that form needs a double cast at every key and erases what the slice exists to carry (#154).
 * Totality is a property of the *literal*, not of the index type.
 */

import type { Entry } from '@/document'

import { journalSlice } from './journal/slice'
import { taskSlice } from './task/slice'
import { personSlice } from './person/slice'
import {
  artistSlice,
  bookSlice,
  labelSlice,
  movieSlice,
  releaseSlice,
} from './media/slice'
import type { JournalDraft } from './journal/write-spec'
import type { TaskDraft } from './task/write-spec'
import type { PersonDraft } from './person/slice'
import type { MediaDraft } from './media/write-spec'
import type { EntryKind, EntryOf, EntrySlice } from './types'

/**
 * Every declared kind keyed to the draft its slice edits.
 *
 * A second map rather than a second parameter on `EntryOf`, because the two are not the same
 * partition: five kinds share `MediaDraft` while each has its own entry type. It also has to exist
 * for `Registry` to be written at all — `EntrySlice` is **contravariant** in its draft parameter
 * (`TDraft` appears only in `onSave`'s argument), so a single shared draft type in the mapped type
 * is not assignable from the five real slices, and `never` is not either. Same class of finding as
 * #154's on the entry parameter, one parameter over.
 */
export interface DraftOf {
  journal: JournalDraft
  task: TaskDraft
  person: PersonDraft
  book: MediaDraft
  movie: MediaDraft
  artist: MediaDraft
  release: MediaDraft
  label: MediaDraft
}

/** The total map. One key per declared kind, each bound to its own entry type and draft. */
export type Registry = {
  [K in keyof EntryOf]: EntrySlice<EntryOf[K], DraftOf[K]>
}

export const registry = {
  journal: journalSlice,
  task: taskSlice,
  person: personSlice,
  book: bookSlice,
  movie: movieSlice,
  artist: artistSlice,
  release: releaseSlice,
  label: labelSlice,
} satisfies Registry

export type AnySlice = (typeof registry)[EntryKind]

/**
 * The door, and the only place a raw `entry.type` is narrowed.
 *
 * `registry[entry.type]` does not compile: `Entry.type` is declared `EntryType | string`
 * (`document/schema/schema.ts:53`), so indexing gives
 * `TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used
 * to index type 'Registry'`. #151 narrowed the union; nothing narrowed the field, and no planning
 * document connected the two. `search-result-row.tsx` had already worked around it by hand, with a
 * cast plus a `??`.
 *
 * **And the data really does have a ninth kind.** `useCreateRelease` writes `type: 'album'`
 * (#395) and documents hold it. So the narrow is a runtime one, `undefined` is a real answer, and
 * it happens once here instead of eight times by cast.
 *
 * Narrowing `Entry.type` in the schema instead would fix neither: it would not touch documents
 * already written, and `Entry` is `document/` vocabulary rather than this domain's.
 */
export function sliceFor(entry: Entry): AnySlice | undefined {
  return registry[entry.type as EntryKind]
}
